import { AnnouncementsProvider } from "@amx/ux-twoui-accessibility";
import { useTheme } from "@amx/ux-twoui-theming";
import {
    IModalProps,
    Modal as FluentModal,
    Stack
} from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import React, { useEffect, useRef } from "react";
import { useComponentLogger } from "../../shared/logging";
import { validateProps } from "../../shared/props";
import { warnDeprecation } from "../../shared/warnDeprecation";
import { Announced } from "../Announced/Announced";
import { DialogFooterWithOperation } from "./blocks/DialogFooter/DialogFooter";
import { DialogCloseButton, DialogHeader } from "./blocks/DialogHeader/DialogHeader";
import { DialogSubText } from "./blocks/DialogSubText/DialogSubText";
import {
    defaultDialogBodyStyle,
    dialogBodySelector,
    getBodyContentStyles,
    getMainContentStyles,
    getScrollableBodyStyles,
    getStickyHeaderFooterModalStyles,
    getStyleForModalProps
} from "./Dialog.styles";
import { DialogBodyStyle, DialogProps } from "./Dialog.types";

export const Dialog: React.FC<DialogProps> = ({
    isBlocking = true,
    showOverlay = true,
    headerLeftRightPadding,
    headingTag,
    withHeaderHorizontalLine,
    dialogStylesTemp,
    bodyStyleTemp,
    topOffsetFixed = false,
    minBodyHeight,
    staticBodyWidth,
    isOpen = false,
    isModeless = false,
    onDismiss,
    onDismissed,
    title,
    searchBoxProps,
    subtitle,
    subText,
    contentId,
    closeButton,
    backButton,
    dialogFooterProps,
    operationData,
    stickyHeaderFooter,
    removeBodyBottomPadding,
    borderRadius = "8px",
    hostId,
    fullScreenMode,
    children,
    ...otherProps
}) => {
    useComponentLogger("Dialog", otherProps.id);

    validateProps("Dialog", otherProps);
    const previousFocusRef = useRef(document.activeElement as HTMLElement | null);

    useEffect(() => {
        if (stickyHeaderFooter?.bodyMinHeight) {
            warnDeprecation("stickyHeaderFooter.bodyMinHeight has been deprecated, please use the new general property bodyMinHeight at the top level.");
        }
        return () => {
            if (previousFocusRef.current) {
                previousFocusRef.current.focus();
            }
        };
    }, [stickyHeaderFooter?.bodyMinHeight]);

    const titleAriaId = useId("twoui");
    const theme = useTheme();

    const dialogBodyStyle: DialogBodyStyle = {
        padding: bodyStyleTemp ? bodyStyleTemp.padding : defaultDialogBodyStyle.padding,
        smallScreenPadding: bodyStyleTemp ? bodyStyleTemp.smallScreenPadding : defaultDialogBodyStyle.smallScreenPadding,
        removeBodyBottomPadding: removeBodyBottomPadding
    };
    const contentStyleSets = getBodyContentStyles(dialogBodyStyle);

    const modalProps: IModalProps = {
        // Override user provided prop to workaround FluentUI forcing
        // alertdialog role for isBlocking
        isBlocking: false,
        styles: getStyleForModalProps(
            theme,
            minBodyHeight,
            staticBodyWidth,
            dialogStylesTemp, //note to change this to modal props object with these items inside
            borderRadius,
            fullScreenMode
        ),
        topOffsetFixed,
        isOpen,
        onDismiss,
        onDismissed,
        isModeless,
        overlay: {
            styles: {
                root: {
                    opacity: showOverlay ? undefined : 0,
                },
            },
            onClick: undefined,
        },
        isClickableOutsideFocusTrap: false,
        titleAriaId,
        containerClassName: (stickyHeaderFooter ? getStickyHeaderFooterModalStyles(stickyHeaderFooter).main : getMainContentStyles(theme)),
        layerProps: {
            eventBubblingEnabled: true,
            /**
             * This hostId is used to link the modal to our theme provider.
             * This allows focus to target the first interactive element when using keyboard navigation, without it we would experience accessibility bugs
             * FluentUI is currently investigating long term fix for this which is being tracked in Bug 15780 (https://dev.azure.com/microsoftdesign/fluent-ui/_workitems/edit/15780)
             */
            hostId: hostId
        },
        allowTouchBodyScroll: true,
        // Set dialog body with sticky header footer as first focusable element
        // to allow keyboard scroll on dialog body
        firstFocusableSelector: stickyHeaderFooter?.firstFocusOnContent ? dialogBodySelector : undefined,
    };

    const interimOnDismiss = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (onDismiss && event) {
            onDismiss(event);
        }
    };

    return (
        <FluentModal {...modalProps}>
            <AnnouncementsProvider announcedComponent={Announced}>
                <DialogHeader
                    title={title}
                    titleAriaId={titleAriaId} //change this to dialog header
                    searchBoxProps={searchBoxProps}
                    subtitle={subtitle}
                    closeButton={closeButton}
                    backButton={backButton}
                    headerLeftRightPadding={headerLeftRightPadding}
                    headingTag={headingTag}
                    withHeaderHorizontalLine={withHeaderHorizontalLine}
                    operationData={operationData ?
                        {
                            progressViewOperationData: operationData.progressViewOperationData,
                            errorDialogTitle: operationData.errorDialogProps?.title,
                            successDialogTitle: operationData.successDialogProps?.title,
                            progressDialogTitle: operationData.progressDialogProps?.title
                        } : undefined
                    }
                    onCloseButtonClicked={interimOnDismiss}
                />
                <Stack
                    id={contentId}
                    className={
                        stickyHeaderFooter?.firstFocusOnContent ?
                            `${contentStyleSets} ${dialogBodySelector}` :
                            contentStyleSets
                    }
                    styles={getScrollableBodyStyles(stickyHeaderFooter, minBodyHeight)}
                    // Allow dialog body with sticky header footer to become focusable for keyboard scroll
                    tabIndex={stickyHeaderFooter?.firstFocusOnContent ? -1 : undefined}
                >
                    <DialogSubText
                        subText={subText}
                        operationData={operationData ?
                            {
                                progressViewOperationData: operationData.progressViewOperationData,
                                errorSubText: operationData.errorDialogProps.subText,
                                successSubText: operationData.successDialogProps?.subText,
                                progressSubText: operationData.progressDialogProps?.subText
                            } : undefined
                        }
                    />
                    {children}
                </Stack>
                <DialogFooterWithOperation
                    operationData={operationData}
                    footerProps={dialogFooterProps}
                />
                <DialogCloseButton closeButton={closeButton} onCloseButtonClicked={interimOnDismiss} headerLeftRightPadding={headerLeftRightPadding} />
            </AnnouncementsProvider>
        </FluentModal>
    );

};
